/**
 * The Poker room's cards — Level 1, and the whole of the front door.
 *
 * ========================= WHAT A CARD IS, EXACTLY =============================
 * A card is a NAME, one line, at most two dials, and a `Setup` — the same `Setup` the
 * old `TableSetup` form built, field for field. That is the point of this file: the
 * eleven decisions the form asked for are answered here, by people who know the answer,
 * and the player is asked for at most two of them.
 *
 * Every field of `Setup` is accounted for. The ones a card fixes are in `base` below;
 * the ones a card offers are `dials`; the rest are the player's, from the Settings
 * sheet (hero seat, fast bots, seed, seed lock) or from Custom Table (everything). The
 * mapping table in `reports/PHASE2B-REPORT.md` is this file, in prose.
 *
 * ## Difficulty is 1-5 here and 0-1 in the engine
 *
 * `tableOf(count, difficulty)` has always taken a 0-1 float, and the master plan §4
 * says the player sees one dial, 1-5. `DIFFICULTY_STEPS` is the whole of the
 * conversion, in one place, and 3 lands on 0.5 — which is what `DEFAULT_SETUP` has
 * always meant by "a normal table".
 *
 * ## Nothing here names an archetype
 *
 * The product's own rule, and a good one: a card says "tough", never "three nits and a
 * maniac". The reveal at the end of the session is where that is earned.
 * ===============================================================================
 */
import type { Setup } from "../lib/setup";
import { DEFAULT_SETUP } from "../lib/setup";

/** The dial's five stops, as the engine's 0-1 difficulty. 3 is `DEFAULT_SETUP`'s 0.5. */
export const DIFFICULTY_STEPS = [0.1, 0.3, 0.5, 0.7, 0.9] as const;

export const DIFFICULTY_WORDS = [
  "Very soft", "Soft", "Mixed", "Tough", "Very tough",
] as const;

/** 1-5 in, 0-1 out. Clamped, because a saved card from an older build could be junk. */
export function difficultyOf(step: number): number {
  const i = Math.min(5, Math.max(1, Math.round(step))) - 1;
  return DIFFICULTY_STEPS[i]!;
}

/** And back again, for a Custom setup being shown on a dial. */
export function stepOfDifficulty(d: number): number {
  let best = 1;
  for (let i = 0; i < DIFFICULTY_STEPS.length; i++) {
    if (Math.abs(DIFFICULTY_STEPS[i]! - d) < Math.abs(DIFFICULTY_STEPS[best - 1]! - d)) best = i + 1;
  }
  return best;
}

/**
 * A dial on a card. At most two, per §2 — and the count is checked at module load,
 * because "at most two visible dials" is a product rule and product rules rot silently.
 */
export type Dial =
  | { kind: "difficulty"; default: number }
  | { kind: "seats"; options: number[]; default: number }
  | { kind: "depth"; options: number[]; default: number };

export interface PlayCard {
  id: string;
  title: string;
  /** One line. What the player wants, not what the engine does. */
  line: string;
  /** The fields this card decides. Everything else comes from Settings or the default. */
  base: Partial<Setup>;
  dials: Dial[];
  /** Tournament cards carry the Turbo badge. See `PresetCard` for what it says today. */
  turbo?: boolean;
  /**
   * What this seat costs in Buster Bucks, for a TOURNAMENT card only.
   *
   * A cash card has no buy-in here because it does not need one: at a cash table a chip
   * is a Buck, so the seat costs its starting stack and `seatCost` does the arithmetic
   * — 100 big blinds at 1/2 is 200, at 2/5 it is 500. A number typed beside a stack it
   * is supposed to equal is a number that will one day disagree with it.
   *
   * A tournament's chips are NOT Bucks, so its buy-in is a separate fact and it is
   * stated here. It funds a Bucks prize pool and nothing else — see `prizePool` and
   * decision D7: these tournaments have no real prize, which is the only reason they
   * may take Bucks at all. The scheduled tournaments in migration 0006 do have real
   * prizes, are free to enter, and take none.
   */
  buyin?: number;
  /** A two-word chip beside the title: the scan-line version of `guardrail`. */
  chip?: string;
  /** The big one. Rendered wider, first, and it is the only card with this set. */
  hero?: boolean;
  /** The card is drawn, and does nothing, until the model behind it passes its gate. */
  verifying?: string;
  /** A guardrail printed on the card itself, verbatim from the code it came from. */
  guardrail?: string;
  /** The Train card this one sharpens. Drives the "Sharpen this" strip. */
  drill?: string;
}

/**
 * The guardrail on the modelled tables, WORD FOR WORD from `TableSetup.tsx`.
 *
 * It is quoted rather than rewritten on purpose. This sentence has been reviewed, it is
 * the one the product has been making since the setup form was written, and a paraphrase
 * of a claim about accuracy is a new claim about accuracy.
 */
export const MODELLED_GUARDRAIL =
  "These bots play a heuristic model, not a solve. The solved charts are in the " +
  "trainer; nothing here is described as GTO because it isn't.";

/**
 * ================= PHASE 5, BOX 0(e): TOURNAMENT CHIPS ARE NOT BUCKS ============
 * A Sit & Go cost 200 Bucks and sat you down behind **50 chips**, because every
 * tournament card was written at a big blind of 2 — the cash tables' blind, on a table
 * whose chips are not money. The Phase 4 review's words: tournament chips read as
 * pocket change. They do, and worse than that they read as BUCKS, which is the one
 * thing they are not: a cash chip is a Buck and a tournament chip is a tournament chip,
 * and the whole reason a Bucks buy-in may fund a Bucks pool at all is that those two
 * are different objects (decision D7).
 *
 * So each tournament card now names a blind big enough that its stack is a stack:
 *
 *   | card            | depth  | blind   | starting stack |
 *   | Sit & Go        | 25 bb  | 20/40   | 1,000          |
 *   | Bubble Pressure | 12 bb  | 50/100  | 1,200          |
 *   | Final Table     | 20 bb  | 50/100  | 2,000          |
 *   | Heads-Up        | dial   | 20/40   | 400 - 1,000    |
 *
 * Three things follow, and all three were checked rather than assumed:
 *
 * 1. **The game is unchanged.** Every quantity the engine reasons about is in big
 *    blinds, and the depth in big blinds has not moved. `Game` has no blind schedule
 *    at all — see the note below — so there is nothing to rescale but the ante.
 *
 * 2. **The ante is ABSOLUTE chips and had to be scaled by hand.** `Hand` posts
 *    `rules.ante` directly, so 0.25 at a blind of 2 is an eighth of a big blind and
 *    0.25 at a blind of 100 is a rounding error. 12.5 keeps the two bubble cards
 *    playing exactly the game they played before. Half a chip is what an eighth of a
 *    hundred is; the felt has always printed a half where there is one.
 *
 * 3. **The wallet does not notice.** A tournament seat costs its `buyin`, which is a
 *    field here and not derived from the stack, and `settleAmount` values a chip at
 *    `buyin / startChips` — a ratio, so it cancels. `tests/wallet-client.test.ts`
 *    still runs both copies of that formula over random tables.
 *
 * ------------------------- WHAT THE ENGINE ACTUALLY DOES -----------------------
 * The review asked whether the level schedule scales with the blind. **There is no
 * level schedule on this route.** `engine/tournament.ts` has one — `BlindLevel[]` and
 * `handsPerLevel`, with the blinds doubling past the end of the list — and `/play`
 * does not use that class. It builds a `Game`, whose `TableRules` are fixed for the
 * life of the session: `format: "tournament"` changes exactly two things, that a
 * busted seat is not topped back up and that the session finishes when one player is
 * left. So the blinds on these cards never go up, and setting them higher rescales the
 * whole session rather than its first level. Wiring `Tournament` behind the Play cards
 * is Phase C's vertical slice; it is written down as an open question in the report.
 * ===============================================================================
 */
/**
 * ============ PHASE 5, BOX 4: A CASH TABLE KEEPS ITS STACKS ====================
 * Both cash cards carried `resetStacks: true`, which puts every seat back to the
 * starting stack before every hand. The Phase 4 review's answer to question 2 is the
 * whole argument and it is one sentence long: **a buy-in that resets every hand is not
 * a buy-in.** You cannot lose a 200-Buck seat at a table that hands it back after each
 * hand, and you cannot win anything at one either — the settlement is capped by what
 * was on the table and the table is reset, so the number in the Ledger was never the
 * number the player played for.
 *
 * The engine's own rule fills the rest in. `Game.startHand()` tops a busted seat back
 * up at a CASH table whether or not stacks reset, so a bot that goes broke rebuys
 * silently, which is what a cash table does. The hero is the one seat that must not be
 * quietly rebought with somebody else's money: `Play.tsx` pauses at hand end when their
 * stack reaches zero and offers the rebuy as a second buy-in on the same nonce, and
 * declining it ends the sitting and opens the Ledger.
 *
 * The flag is still on `Setup` and Custom Table still offers it, because a practice
 * table at a constant depth is a real thing to want — the reason it was the default is
 * in the engine's own comment. It is no longer what a named cash card does.
 * ===============================================================================
 */
export const CASH_KEEPS_STACKS = false;

export const PLAY_CARDS: readonly PlayCard[] = [
  {
    id: "quick",
    title: "Quick Play",
    line: "Six seats, a normal table, straight to a hand.",
    hero: true,
    /* `resetStacks: false` — Phase 5 box 4. See the note above `CASH_KEEPS_STACKS`. */
    base: { seats: 6, format: "cash", stackBb: 100, bigBlind: 2, ante: 0, resetStacks: false },
    dials: [{ kind: "difficulty", default: 3 }],
    drill: "pushfold",
  },
  {
    id: "sng",
    title: "Sit & Go",
    line: "Nine seats, 1,000 chips at 20/40, played until one is left.",
    base: { seats: 9, format: "tournament", stackBb: 25, bigBlind: 40, ante: 0 },
    buyin: 200,
    dials: [{ kind: "difficulty", default: 3 }],
    turbo: true,
    drill: "pushfold",
  },
  {
    id: "bubble",
    title: "Bubble Pressure",
    line: "1,200 chips at 50/100, one place off the money, everybody scared of everybody.",
    base: { format: "tournament", stackBb: 12, bigBlind: 100, ante: 12.5 },
    buyin: 200,
    dials: [
      { kind: "seats", options: [6, 9], default: 6 },
      { kind: "difficulty", default: 3 },
    ],
    turbo: true,
    drill: "bubble",
  },
  {
    id: "final",
    title: "Final Table",
    line: "Nine left, 2,000 chips at 50/100, and the stacks get shorter as they go.",
    base: { seats: 9, format: "tournament", stackBb: 20, bigBlind: 100, ante: 12.5 },
    buyin: 500,
    dials: [{ kind: "difficulty", default: 3 }],
    turbo: true,
    drill: "bubble",
  },
  {
    id: "heads-up",
    title: "Heads-Up",
    line: "Two players at 20/40, every hand, no hiding.",
    base: { seats: 2, format: "tournament", bigBlind: 40, ante: 0 },
    buyin: 100,
    dials: [
      { kind: "depth", options: [10, 15, 20, 25], default: 15 },
      { kind: "difficulty", default: 3 },
    ],
    drill: "headsup",
  },
  {
    id: "bounty",
    title: "Bounty Hunter",
    line: "Knockout tournaments, where every player at the table has a price.",
    base: {},
    dials: [],
    verifying: "Verifying the model",
    drill: "bounty",
  },
  {
    id: "cash",
    title: "Cash Table",
    line: "Deep stacks, busted seats rebuy, and the hand goes to the river.",
    /* 2/5, where Quick Play is 1/2 — the two cash cards are the same game at two
       stake levels, which is the only thing that made them different cards in the
       first place and is now visible on them: 200 Bucks against 500. */
    base: { seats: 6, format: "cash", stackBb: 100, bigBlind: 5, ante: 0, resetStacks: false },
    dials: [{ kind: "difficulty", default: 3 }],
    /* The chip is the scan-line, the sentence is the guardrail. Both, per the
       Phase 2b review's answer to open question 1. */
    chip: "Modelled, not solved",
    guardrail: MODELLED_GUARDRAIL,
    drill: "pushfold",
  },
];

/**
 * A card plus the dials the player has moved, as the `Setup` the engine takes.
 *
 * `DEFAULT_SETUP` is the floor, the card's `base` is next, the dials are last, and the
 * player's own Level 3 values (hero seat, fast bots, seed, seed lock) are handed in by
 * the caller — which is the only place those four can come from, because they are
 * settings and not properties of the card.
 */
export function setupFor(
  card: PlayCard,
  dials: { difficulty?: number; seats?: number; depth?: number },
  level3: Pick<Setup, "heroSeat" | "fastBots" | "seed" | "seedLocked">,
): Setup {
  const base: Setup = { ...DEFAULT_SETUP, ...card.base } as Setup;
  const seats = dials.seats ?? base.seats;
  const stackBb = dials.depth ?? base.stackBb;
  const difficulty = dials.difficulty === undefined
    ? base.difficulty : difficultyOf(dials.difficulty);
  return {
    ...base,
    seats,
    stackBb,
    difficulty,
    ...level3,
    /* The hero cannot sit in a seat that does not exist. The form clamped this too. */
    heroSeat: Math.min(level3.heroSeat, seats - 1),
    /* A tournament never resets stacks; the engine ignores it, and the card should not
       claim otherwise. Same rule `Play.tsx` applies when it builds the config.
       The fallback is `CASH_KEEPS_STACKS` now rather than `true` — a card that says
       nothing about resetting should get what a cash table does (box 4), not what
       `DEFAULT_SETUP` happened to be written with in Phase 2b. */
    resetStacks: base.format === "cash" && (base.resetStacks ?? CASH_KEEPS_STACKS),
  };
}

/**
 * The card as the wallet sees it: format, seats, blind, depth, and a buy-in if it has
 * one. `lib/wallet-client.ts` turns that into what the seat costs.
 *
 * Derived from the card and the dials rather than from a built `Setup`, because the
 * lobby has no Level 3 values to hand and does not need any: none of the four change
 * what a seat costs.
 */
export function seatShape(card: PlayCard, dials: { seats?: number; depth?: number }): {
  format: "cash" | "tournament"; seats: number; bigBlind: number; stackBb: number; buyin?: number;
} {
  return {
    format: card.base.format ?? "cash",
    seats: dials.seats ?? card.base.seats ?? 6,
    bigBlind: card.base.bigBlind ?? 2,
    stackBb: dials.depth ?? card.base.stackBb ?? 100,
    buyin: card.buyin,
  };
}

/** The default dial positions for a card, before the player touches anything. */
export function defaultDials(card: PlayCard): { difficulty?: number; seats?: number; depth?: number } {
  const out: { difficulty?: number; seats?: number; depth?: number } = {};
  for (const d of card.dials) {
    if (d.kind === "difficulty") out.difficulty = d.default;
    if (d.kind === "seats") out.seats = d.default;
    if (d.kind === "depth") out.depth = d.default;
  }
  return out;
}

/* --------------------------------------------------------------- the drills --- */

/**
 * A Train card is a PRESET on the trainer that already exists, never a new drill.
 *
 * The trainer's selection is (game type, structure, table size, situation, stack) and
 * every one of these cards is one point in it. `icm` is matched rather than pinned to a
 * `payout_preset`: which payout presets are published is a property of the chart
 * library on the day, and a card that names one is a card that breaks when the library
 * changes. The trainer keeps its own rule — an unavailable selection falls back to what
 * IS published — so a card can only ever be a starting point, which is what it is.
 */
export interface TrainCard {
  id: string;
  title: string;
  line: string;
  /** Passed to `Train` as its opening selection. Undefined fields are the trainer's. */
  preset?: { gameType?: "regular" | "knockout"; tableSize?: number; icm?: string | null; depth?: number };
  /** Opens the trainer with its full facet rail rather than a preset. Level 2. */
  build?: boolean;
  verifying?: string;
}

export const TRAIN_CARDS: readonly TrainCard[] = [
  {
    id: "pushfold",
    title: "Push/Fold Basics",
    line: "Shove or fold at 15 big blinds, nine-handed, chips are chips.",
    preset: { gameType: "regular", tableSize: 9, icm: null, depth: 15 },
  },
  {
    id: "bubble",
    title: "Bubble Survival",
    line: "The same hands, one place off the money, where folding starts to pay.",
    preset: { gameType: "regular", tableSize: 9, icm: "bubble", depth: 12 },
  },
  {
    id: "bounty",
    title: "Bounty Decisions",
    line: "What a head is worth, and how much wider it makes you call.",
    preset: { gameType: "knockout" },
    verifying: "Verifying the model",
  },
  {
    id: "headsup",
    title: "Heads-Up Sim",
    line: "Two-handed shove and call, at the depth the last hand is played at.",
    preset: { gameType: "regular", tableSize: 2, icm: null, depth: 15 },
  },
  {
    id: "gauntlet",
    /*
     * Renamed in Phase 4. It is the existing preflop drill at the shallowest published
     * depth — no timer, no streak, no gauntlet — and a name that promises a mode the
     * card does not have is the same lie as a dead switch. *Turbo Gauntlet* comes back
     * when there is a clock behind it.
     */
    title: "Short Stacks",
    line: "The shallowest stacks in the library, one spot after another.",
    preset: { gameType: "regular", tableSize: 9, icm: null, depth: 10 },
  },
  {
    id: "build",
    title: "Build a Drill",
    line: "Every facet the library has: format, table, situation, stack.",
    build: true,
  },
];

/* ------------------------------------------------------------------ checks --- */

/**
 * Product rules, checked at module load rather than in a test.
 *
 * Both of them are things a future card would break silently and a screenshot would
 * not show: a third dial (§2's "at most two"), and a card whose drill link points at a
 * Train card that does not exist (the "Sharpen this" strip would go nowhere).
 */
for (const c of PLAY_CARDS) {
  /**
   * A tournament card that names no buy-in would deal a free seat under a heading that
   * says otherwise on every other card. Checked at module load for the same reason the
   * dial count is: it is a product rule, and product rules rot silently.
   *
   * `verifying` cards are exempt — they have no table behind them at all.
   */
  if (!c.verifying && c.base.format === "tournament" && !(c.buyin! > 0)) {
    throw new Error(`Play card "${c.id}" is a tournament and names no Bucks buy-in.`);
  }
  /**
   * A tournament's chips must not be able to pass for Bucks.
   *
   * Phase 5 box 0(e): the cards were all written at a big blind of 2, which is the
   * CASH tables' blind, and it put fifty chips in front of a player who had just paid
   * two hundred Bucks for the seat. The two numbers are different kinds of thing and
   * the felt has to show that. Twenty is the floor rather than the exact figure each
   * card uses, because the depth dial is the other half of the stack and this is a
   * guard against the old default coming back, not a second opinion about the blinds.
   */
  if (!c.verifying && c.base.format === "tournament" && !((c.base.bigBlind ?? 0) >= 20)) {
    throw new Error(
      `Play card "${c.id}" is a tournament at a big blind of ${c.base.bigBlind} — ` +
      "tournament chips are not Bucks and must not be counted in twos.");
  }
  if (c.dials.length > 2) {
    throw new Error(`Play card "${c.id}" has ${c.dials.length} dials; §2 allows two.`);
  }
  if (c.drill && !TRAIN_CARDS.some((t) => t.id === c.drill)) {
    throw new Error(`Play card "${c.id}" sharpens "${c.drill}", which is not a Train card.`);
  }
}
